import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import emailjs from '@emailjs/browser';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    subject: '',
    message: '',
  });
  const [isSending, setIsSending] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSending) return;
    
    setIsSending(true);

    const fullMessage = `이메일 주소: ${formData.email}\n연락처: ${formData.phoneNumber}\n\n[문의 내용]\n${formData.message}`;

    try {
      await emailjs.send(
        'service_243k76q',
        'template_ln0gk8n',
        {
          name: formData.name,
          email: formData.email,
          title: formData.subject,
          message: fullMessage,
        },
        '4IpKtWfnb6HwhCl3D'
      );
      
      alert('문의가 성공적으로 전송되었습니다! 빠른 시일 내에 답변해 드리겠습니다.');
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Email sending failed:', error);
      alert('문의 메일 전송에 실패했습니다. 이메일(freakit2025@gmail.com)로 직접 문의해 주시면 감사하겠습니다.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>문의하기</HeroTitle>
          <HeroDescription>
            궁금한 점이 있으신가요? <br />
            언제든지 연락주세요!
          </HeroDescription>
        </HeroContent>
      </HeroSection>

      <FormSection>
        <ContactGrid>
          <ContactInfo>
            <SectionTitle>연락처 정보</SectionTitle>
            
              <ScrollFadeIn direction="right" delay={0.1}>
                <InfoItem>
                  {/* 아이콘 교체 */}
                  <InfoIcon><Mail /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>이메일</InfoLabel>
                    {/* 정보 업데이트 */}
                    <InfoValue>freakit2025@gmail.com</InfoValue>
                  </InfoContent>
                </InfoItem>
              </ScrollFadeIn>

              <ScrollFadeIn direction="right" delay={0.2}>
                <InfoItem>
                  {/* 아이콘 교체 */}
                  <InfoIcon><Phone /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>전화</InfoLabel>
                    {/* 정보 업데이트 */}
                    <InfoValue>+82 10-7568-3302</InfoValue>
                  </InfoContent>
                </InfoItem>
              </ScrollFadeIn>

              <ScrollFadeIn direction="right" delay={0.3}>
                <InfoItem>
                  {/* 아이콘 교체 */}
                  <InfoIcon><MapPin /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>주소</InfoLabel>
                    <InfoValue>
                      {/* 정보 업데이트 */}
                      대전광역시 유성구 대학로 291<br />
                      KAIST W8 교육지원동
                    </InfoValue>
                  </InfoContent>
                </InfoItem>
              </ScrollFadeIn>

              <ScrollFadeIn direction="right" delay={0.4}>
                <InfoItem>
                  {/* 아이콘 교체 */}
                  <InfoIcon><Clock /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>운영 시간</InfoLabel>
                    <InfoValue>
                      평일 09:00 - 18:00<br />
                      주말 및 공휴일 휴무
                    </InfoValue>
                  </InfoContent>
                </InfoItem>
              </ScrollFadeIn>
          </ContactInfo>

          <ScrollFadeIn direction="left" delay={0.2}>
            <ContactForm onSubmit={handleSubmit}>
              <FormTitle>메시지 보내기</FormTitle>
              
              <FormGroup>
                <Label htmlFor="name">이름 *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="홍길동"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">이메일 *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="example@email.com"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phoneNumber">연락처 *</Label>
                <Input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  placeholder="010-0000-0000"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">제목 *</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="문의 제목을 입력하세요"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">메시지 *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="문의 내용을 입력하세요"
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSending}>
                {isSending ? '전송 중...' : '전송하기'}
              </SubmitButton>
            </ContactForm>
          </ScrollFadeIn>
        </ContactGrid>
      </FormSection>

      <MapSection>
        <SectionTitle>오시는 길</SectionTitle>
        {/* 지도 삽입 */}
        <MapContainer>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3214.2841234567890!2d127.35759331531842!3d36.36996478004445!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35654bb7bf479483%3A0x36d2732f1f0dcbc0!2z6rWQ7Jyh7KeA7JuQ64-ZKFc4KQ!5e0!3m2!1sko!2skr!4v1632987654321!5m2!1sko!2skr"
            width="100%"
            height="100%" // MapContainer의 height (400px)를 따릅니다.
            style={{ border: 0 }}
            allowFullScreen={true}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="KAIST W8 Location Map"
          ></iframe>
        </MapContainer>
      </MapSection>
    </PageContainer>
  );
};

export default ContactPage;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  padding-top: 80px;
`;
const HeroSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 1s ease-out;
`;
const HeroTitle = styled.h1`
  font-size: 2.6rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.1rem;
  }
`;
const HeroDescription = styled.p`
  font-size: 1.5rem;
  line-height: 1.8;
  opacity: 0.95;
`;

const FormSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
`;

const ContactGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  background: ${({ theme }) => theme.colors.bgSecondary};
  backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;
const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 4rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 2.25rem 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const InfoIcon = styled.div`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.accent};
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const InfoContent = styled.div``;
const InfoLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.6;
`;
const ContactForm = styled.form`
  background: rgba(255, 255, 255, 0.05);
  padding: 4rem;
  border-left: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 2.25rem 2rem;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const FormTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 2rem;
`;
const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;
const TextArea = styled.textarea`
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: inherit;
  resize: vertical;
  transition: ${({ theme }) => theme.transitions.fast};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;
const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: #ffffff;
  color: #020617;
  font-size: 1.1rem;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.cubic};
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
    background: #f8fafc;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const MapSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
`;

// MapPlaceholder를 MapContainer로 변경
const MapContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  height: 400px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: 14px;
  overflow: hidden; // 지도가 둥근 모서리를 넘지 않도록
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.medium};
`;